import { Autocomplete, AutocompleteProps, ChipTypeMap } from '@mui/material'
import { ElementType } from 'react'
import { Control, FieldPath, FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { CaretDown } from '../../Select/Select'

type FormAutocompleteProps<
    TFieldValues extends FieldValues,
    Value,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
    ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
> = {
    autocompleteProps: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>
    control: Control<TFieldValues>
    name: TName
    rules?: UseControllerProps<TFieldValues, TName>['rules']
    required?: boolean
}

export function FormAutocomplete<
    TFieldValues extends FieldValues,
    Value,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
    ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
>({
    control,
    name,
    autocompleteProps,
    rules,
    required,
}: FormAutocompleteProps<TFieldValues, Value, TName, Multiple, DisableClearable, FreeSolo, ChipComponent>) {
    const validationRules = {
        ...rules,
        ...(required && {
            required: rules?.required || 'This field is required',
        }),
    }
    const { field } = useController({
        name,
        control,
        disabled: autocompleteProps?.disabled,
        rules: validationRules,
    })
    return (
        <Autocomplete
            value={field.value}
            {...autocompleteProps}
            onChange={(event, value, reason, details) => {
                field.onChange(value)
                autocompleteProps.onChange?.(event, value, reason, details)
            }}
            popupIcon={<CaretDown />}
        />
    )
}
